/**
 * AppRoutes — the SINGLE source of truth for the app's child routes.
 *
 * Spec 216 (T003/R2): App.tsx previously declared this entire tree TWICE
 * (dev-bypass branch and production branch) — every route change needed
 * identical dual edits, the exact drift class that broke navigation before.
 * Both branches now render {appChildRoutes} inside their own wrapper
 * (<AppLayout/> vs <ProtectedRoute><AppLayout/></ProtectedRoute>).
 *
 * React Router v6 flattens fragments inside <Routes>, so this fragment's
 * <Route> elements behave exactly as if declared inline.
 */

import React from 'react';
import { Route, Navigate } from 'react-router-dom';
import { ProtectedRoute } from './auth/ProtectedRoute';
import type { UserRole } from '@shared/types/auth';
import type { NavAreaId } from '@shared/access/navAccessPolicy';
import TenantLicensesPage from './pages/admin/TenantLicensesPage';

// Spec 190 gap: the shared UserRole union omits 'manager' (it exists at runtime in
// ProtectedRoute.ROLE_HIERARCHY). Cast for the manager-gated routes until the type is fixed.
const MANAGER: UserRole = 'manager' as UserRole;
const ManagerUp: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ProtectedRoute requiredRole={MANAGER}>{children}</ProtectedRoute>
);

// Spec 216 (T009/FR-003) wrapped every Internal route in a role-only `AdminOnly` guard
// (menu visibility alone is not access control). Spec 285 RETIRED that wrapper: those
// routes now use RequireArea below, which takes the exact rule from the shared policy.

/**
 * Spec 285 — gate a route by the ONE shared navigation access policy (role set ∧ license,
 * SuperAdmin full access). It is the same decision the backend's requireNavAccess makes for
 * the APIs behind the page, so a page hidden from the menu cannot be reached by URL either
 * (visibility == reachability). Internal routes that used AdminOnly now take their exact rule
 * from the policy: Collection Jobs + Infrastructure Sync stay Admin+SuperAdmin; the rest of
 * Internal is SuperAdmin-only (owner, 2026-10-01).
 */
const RequireArea: React.FC<{ area: NavAreaId; children: React.ReactNode }> = ({ area, children }) => (
  <ProtectedRoute requiredArea={area}>{children}</ProtectedRoute>
);

// Pages
import DashboardRouter from './pages/Dashboard/DashboardRouter';
import Workspaces from './modules/backup/pages/Workspaces';
import Lakehouses from './modules/backup/pages/Lakehouses';
import LakehouseDetailPage from './modules/backup/pages/LakehouseDetailPage';
import Warehouses from './modules/backup/pages/Warehouses';
import WarehouseDetailPage from './modules/backup/pages/WarehouseDetailPage';
import BackupSection from './modules/backup/pages/BackupSection';
import RetentionPage from './modules/backup/pages/RetentionPage';
import InfrastructureSyncPage from './pages/operations/InfrastructureSyncPage';
import { WorkflowDashboard } from './modules/workflows/pages/WorkflowDashboard';
import {
  StorageAccountsPage,
  StorageAccountDetailPage,
} from './modules/storage-accounts';
import { DeltaCompare } from './modules/backup/pages/DeltaCompare';
import { Alerts } from './modules/backup/pages/Alerts';
import { Users } from './modules/backup/pages/Users';
import { InfrastructurePage } from './modules/infrastructure/pages/InfrastructurePage';
import { HealthDashboardPage } from './modules/infrastructure/pages/HealthDashboardPage';
import {
  ServicePrincipalsPage,
  ServicePrincipalDetailPage,
  ReconciliationStatusPage,
} from './modules/service-principals';
import { AlertRulesPage } from './pages/Settings/AlertRules';
import { SLAConfiguration } from './pages/Settings/SLAConfiguration';
import MaintenanceDashboard from './pages/MaintenanceDashboard';
import DesignSystemShowcase from './pages/DesignSystem/DesignSystemShowcase';
import OperationsLayout from './pages/operations';
import SLABlastRadiusPage from './pages/operations/SLABlastRadiusPage';
import CollectionJobsPage from './pages/operations/CollectionJobsPage';
import CollectionJobLogsPage from './pages/operations/CollectionJobLogsPage';
import DataPipelineMonitoringPage from './pages/operations/DataPipelineMonitoringPage';
import PipelineMonitoring from './pages/operations/PipelineMonitoring';
import DeploymentHistoryPage from './pages/DeploymentHistory/DeploymentHistoryPage';
import NetworkOverviewPage from './pages/operations/NetworkOverviewPage';
import PrivateEndpointsPage from './pages/operations/PrivateEndpointsPage';
import NsgSummaryPage from './pages/operations/NsgSummaryPage';
import PlatformHealthPage from './pages/operations/PlatformHealthPage';
import GovernanceCompliancePage from './pages/operations/GovernanceCompliancePage';
import CapacityInsightsPage from './pages/insights/CapacityInsightsPage';
import WorkspaceInsightsPage from './pages/insights/WorkspaceInsightsPage';
import ItemInsightsPage from './pages/insights/ItemInsightsPage';
import WarehouseInsightsPage from './pages/insights/WarehouseInsightsPage';
import StorageInsightsPage from './pages/insights/StorageInsightsPage';
import DependenciesTopologyPage from './pages/insights/DependenciesTopologyPage';
import AzureInfraInsightsPage from './pages/insights/AzureInfraInsightsPage';
import CostInsightsPage from './pages/insights/CostInsightsPage';
import GrowthForecastPage from './pages/insights/GrowthForecastPage';
import TelemetryDashboard from './pages/operations/TelemetryDashboard';
import TenantSettingsPage from './pages/operations/governance/TenantSettingsPage';
import WorkspaceAccessPage from './pages/operations/governance/WorkspaceAccessPage';
import SharedArtifactsPage from './pages/operations/governance/SharedArtifactsPage';
import DataLineagePage from './pages/operations/governance/DataLineagePage';
import GatewaysPage from './pages/operations/governance/GatewaysPage';
import ReconciliationDashboard from './pages/Reconciliation/ReconciliationDashboard';
import { JobDashboard } from './pages/JobDashboard';
import { CapacityMetricsDashboard, CapacityDetailPage } from './pages/CapacityMetrics';
import { InfrastructureTrendsPage } from './pages/InfrastructureTrends';
import {
  MetadataExplorerPage,
  SemanticModelDefinitionPage,
  LineageGraphPage,
  RefreshHistoryPage,
  ItemSchedulesPage,
  SemanticModelsListPage,
  LineageListPage,
  RefreshHistoryListPage,
  SchedulesListPage,
} from './pages/Metadata';
import { ClientsPage } from './pages/admin/ClientsPage';
import { ClientDetailPage } from './pages/admin/ClientDetailPage';
import { SupportPage } from './pages/Help/SupportPage';
import DocumentationContainer from './pages/Help/DocumentationContainer';
import GuideViewerPage from './pages/Help/GuideViewerPage';
import RunbooksContainer from './pages/Help/RunbooksContainer';
import { RunbookPlaceholderPage, DocArticlePlaceholderPage } from './pages/Help/ContentPlaceholderPage';
import { SystemAuditLogPage, AuthAuditLogPage } from './pages/Audit';

/**
 * All authenticated child routes, shared verbatim by the dev-bypass and
 * production route trees. (The dev-only `/login` → dashboard redirect stays
 * in App.tsx's dev branch; the prod public routes stay in the prod branch.)
 */
export const appChildRoutes = (
  <>
    <Route index element={<Navigate to='/dashboard' replace />} />
    <Route path='/dashboard' element={<DashboardRouter />} />
    <Route path='/admin/users' element={<RequireArea area='system.users'><Users /></RequireArea>} />
    {/* Spec 285 — SuperAdmin-only license administration */}
    <Route path='/admin/tenant-licenses' element={<RequireArea area='system.tenant-licenses'><TenantLicensesPage /></RequireArea>} />

    {/* Resource Discovery (Feature 104) */}
    <Route path='/capacity/discovery' element={<Navigate to='/operations/collection-jobs' replace />} />
    <Route path='/workspaces/discovery' element={<Navigate to='/operations/collection-jobs' replace />} />
    <Route path='/lakehouses/discovery' element={<Navigate to='/operations/collection-jobs' replace />} />
    <Route path='/warehouses/discovery' element={<Navigate to='/operations/collection-jobs' replace />} />
    <Route path='/storage-accounts/discovery' element={<Navigate to='/operations/collection-jobs' replace />} />
    <Route path='/discovery/runs/:id' element={<Navigate to='/operations/collection-jobs' replace />} />

    {/* Infrastructure Trends (Feature 021) */}
    <Route path='/infrastructure-trends' element={<InfrastructureTrendsPage />} />

    {/* Storage Accounts (Tier 1 root-level entities - Constitution v1.11.0) */}
    <Route path='/storage-accounts' element={<StorageAccountsPage />} />
    <Route path='/storage-accounts/catalog' element={<StorageAccountsPage />} />
    <Route path='/storage-accounts/*' element={<StorageAccountDetailPage />} />

    {/* Core Management */}
    <Route path='/workspaces' element={<Workspaces />} />
    <Route path='/lakehouses' element={<Lakehouses />} />
    {/* Spec 216 T029: catalogs merged into the lists as "Protection" tabs */}
    <Route path='/lakehouses/catalog' element={<Navigate to='/lakehouses?view=protection' replace />} />
    <Route path='/lakehouses/:id' element={<LakehouseDetailPage />} />
    <Route path='/warehouses' element={<Warehouses />} />
    <Route path='/warehouses/catalog' element={<Navigate to='/warehouses?view=protection' replace />} />
    <Route path='/warehouses/:id' element={<WarehouseDetailPage />} />
    <Route path='/policies' element={<Navigate to='/backup/plans' replace />} />
    <Route path='/retention-policies' element={<Navigate to='/settings/data-retention' replace />} />
    <Route path='/settings/data-retention' element={<Navigate to='/backup/retention' replace />} />

    {/* Operations */}
    <Route path='/backup-runs' element={<Navigate to='/backup' replace />} />
    <Route path='/restore-runs' element={<Navigate to='/backup/restore' replace />} />
    <Route path='/recovery-tests' element={<Navigate to='/backup/restore' replace />} />
    {/* Spec 201 — consolidated Backup section */}
    {/* Spec 285 — the whole Backup experience needs the backup license (SuperAdmin exempt) */}
    <Route path='/backup' element={<RequireArea area='backup'><BackupSection /></RequireArea>} />
    <Route path='/backup/retention' element={<RequireArea area='backup'><RetentionPage /></RequireArea>} />
    <Route path='/backup/:tab' element={<RequireArea area='backup'><BackupSection /></RequireArea>} />
    <Route path='/schedules' element={<Navigate to='/backup/schedules' replace />} />
    {/* Spec 216 T034: the Deployments screen (RemoteDeployments) is REMOVED
        ("useless" — owner walkthrough); deployment history lives in Internal. */}
    <Route path='/deployments' element={<Navigate to='/operations/deployment-history' replace />} />
    {/* Internal (Admin-only) — Spec 216 T009 */}
    <Route path='/maintenance' element={<RequireArea area='internal.maintenance'><MaintenanceDashboard /></RequireArea>} />
    <Route path='/reconciliation' element={<RequireArea area='internal.reconciliation'><ReconciliationDashboard /></RequireArea>} />
    <Route path='/jobs' element={<RequireArea area='internal.collection-jobs'><JobDashboard /></RequireArea>} />
    <Route path='/design-system' element={<DesignSystemShowcase />} />
    <Route path='/capacity-metrics' element={<CapacityMetricsDashboard />} />
    <Route path='/capacity-metrics/:capacityId' element={<CapacityDetailPage />} />

    {/* Analytics & Compliance */}
    <Route path='/costs' element={<Navigate to='/backup' replace />} />
    <Route path='/storage-costs' element={<Navigate to='/backup/storage' replace />} />
    <Route path='/discovery' element={<RequireArea area='internal.infrastructure-sync'><InfrastructureSyncPage /></RequireArea>} />
    <Route path='/reports' element={<Navigate to='/backup' replace />} />
    {/* Delta Compare is a Backup Management tab (menu child of Backup) — backup license */}
    <Route path='/compare' element={<RequireArea area='backup'><DeltaCompare /></RequireArea>} />

    {/* Infrastructure — Configuration is Internal (Admin-only); the health
        dashboard stays an Operations screen (Admin/Operator via menu). */}
    <Route path='/infrastructure' element={<RequireArea area='internal.configuration'><InfrastructurePage /></RequireArea>} />
    <Route path='/admin/infrastructure' element={<Navigate to='/infrastructure' replace />} />
    <Route path='/infrastructure/health' element={<RequireArea area='infrastructure-health'><HealthDashboardPage /></RequireArea>} />


    {/* Operational Dashboards (Feature 117) */}
    <Route path='/operations' element={<OperationsLayout />}>
      {/* Spec 217 T011: rewritten as Workspace Insights */}
      <Route path='workspace-inventory' element={<WorkspaceInsightsPage />} />
      {/* Spec 217 T015: rewritten as Item Insights */}
      <Route path='fabric-items' element={<ItemInsightsPage />} />
      {/* Spec 216 T030: schema browser moved to the Warehouse detail "Schema"
          tab; the Insights half of the old screen is a Spec-217 brief. */}
      <Route path='warehouse-schema' element={<Navigate to='/warehouses' replace />} />
      {/* Spec 217 T024: rewritten as Storage Insights */}
      <Route path='storage-analytics' element={<StorageInsightsPage />} />
      {/* Spec 216 T028: Job Monitoring merged INTO Collection Jobs (/jobs) */}
      <Route path='job-monitoring' element={<Navigate to='/jobs' replace />} />
      {/* Spec 217 T041: rewritten as Azure Infrastructure Insights */}
      <Route path='azure-infrastructure' element={<AzureInfraInsightsPage />} />
      <Route path='sla-blast-radius' element={<RequireArea area='clients.sla-blast-radius'><SLABlastRadiusPage /></RequireArea>} />
      <Route path='collection-jobs' element={<RequireArea area='internal.collection-jobs'><CollectionJobsPage /></RequireArea>} />
      <Route path='collection-job-logs' element={<RequireArea area='internal.collection-jobs'><CollectionJobLogsPage /></RequireArea>} />
      <Route path='ods-monitoring' element={<RequireArea area='data-pipeline-monitoring'><DataPipelineMonitoringPage /></RequireArea>} />
      <Route path='pipeline-monitoring' element={<RequireArea area='pipeline-monitoring'><PipelineMonitoring /></RequireArea>} />
      <Route path='deployment-history' element={<RequireArea area='internal.deployment-history'><DeploymentHistoryPage /></RequireArea>} />
      <Route path='network-topology' element={<NetworkOverviewPage />} />
      <Route path='network-topology/private-endpoints' element={<PrivateEndpointsPage />} />
      <Route path='network-topology/nsg-rules' element={<NsgSummaryPage />} />
      <Route path='platform-health' element={<RequireArea area='platform-health'><PlatformHealthPage /></RequireArea>} />
      {/* Governance & Compliance Dashboard (Feature 142) */}
      <Route path='governance' element={<GovernanceCompliancePage />}>
        <Route path='tenant-settings' element={<TenantSettingsPage />} />
        <Route path='workspace-access' element={<WorkspaceAccessPage />} />
        <Route path='shared-artifacts' element={<SharedArtifactsPage />} />
        <Route path='data-lineage' element={<DataLineagePage />} />
        <Route path='gateways' element={<GatewaysPage />} />
      </Route>
      {/* Telemetry & Pipeline Unified Dashboard (Feature 154+155) */}
      <Route path='telemetry' element={<TelemetryDashboard />} />
      {/* Legacy redirects — keep old bookmarks working */}
      <Route path='capacity-telemetry' element={<Navigate to='/operations/telemetry' replace />} />
      {/* Spec 216 T009: Capacity Portfolio moved to Insights (/insights/capacity) */}
      <Route path='capacity-portfolio' element={<Navigate to='/insights/capacity' replace />} />
      <Route path='telemetry-pipelines' element={<Navigate to='/operations/telemetry?tab=health' replace />} />
      <Route path='telemetry-pipelines/setup-guide' element={<Navigate to='/operations/telemetry?tab=configure' replace />} />
      <Route path='telemetry-pipelines/configuration' element={<Navigate to='/operations/telemetry?tab=configure' replace />} />
      <Route path='telemetry-pipelines/management' element={<Navigate to='/operations/telemetry?tab=health' replace />} />
      <Route path='telemetry-pipelines/insights' element={<Navigate to='/operations/telemetry?tab=insights' replace />} />
    </Route>

    {/* Insights (Spec 216) — the renamed Operational Dashboards family.
        Capacity Insights (T040) is the rewritten pilot that replaced the
        Capacity Portfolio screen. */}
    <Route path='/insights' element={<OperationsLayout />}>
      <Route index element={<Navigate to='/insights/capacity' replace />} />
      <Route path='capacity' element={<CapacityInsightsPage />} />
      {/* Spec 217 T019: fills the RESERVED Warehouse Insights slot */}
      <Route path='warehouse' element={<WarehouseInsightsPage />} />
      {/* Spec 217 T053: new-analytics-catalog build */}
      <Route path='cost' element={<ManagerUp><CostInsightsPage /></ManagerUp>} />
      {/* Spec 217 T057: new-analytics-catalog build */}
      <Route path='growth-forecast' element={<GrowthForecastPage />} />
    </Route>

    {/* Service Principal Registry (Constitution v1.8.0 - Module 1) — Internal/Admin-only */}
    <Route path='/service-principals' element={<RequireArea area='internal.service-principals'><ServicePrincipalsPage /></RequireArea>} />
    <Route path='/service-principals/:id' element={<RequireArea area='internal.service-principals'><ServicePrincipalDetailPage /></RequireArea>} />
    <Route path='/service-principals/reconciliation' element={<RequireArea area='internal.sp-reconciliation'><ReconciliationStatusPage /></RequireArea>} />

    {/* Workflow Dashboard (Feature 135) */}
    <Route path='/workflows' element={<RequireArea area='operations.workflows'><WorkflowDashboard /></RequireArea>} />

    {/* System */}
    <Route path='/alerts' element={<Alerts />} />
    {/* Spec 216 T034: the Settings landing is REMOVED — children re-homed
        (Alert Rules keeps a System nav entry; SLA Config → Clients). */}
    <Route path='/settings' element={<Navigate to='/settings/alert-rules' replace />} />
    <Route path='/settings/sla' element={<RequireArea area='clients.sla-configuration'><SLAConfiguration /></RequireArea>} />
    {/* Spec 216 T036: Code Quality (Feature 099) REMOVED — owner-confirmed */}
    <Route path='/settings/code-quality' element={<Navigate to='/settings/alert-rules' replace />} />
    <Route path='/settings/alert-rules' element={<RequireArea area='system.alert-rules'><AlertRulesPage /></RequireArea>} />
    <Route path='/audit' element={<Navigate to='/audit/system' replace />} />
    <Route path='/audit/system' element={<RequireArea area='system.system-logs'><SystemAuditLogPage /></RequireArea>} />
    <Route path='/audit/auth' element={<RequireArea area='system.system-logs'><AuthAuditLogPage /></RequireArea>} />
    <Route path='/users' element={<Navigate to='/admin/users' replace />} />

    {/* Capacity Module (Feature 102-screen-completeness) — Spec 216 T034:
        Executive Dashboard (permanent ComingSoonBanner) and /capacity/list
        (duplicate of /capacity-metrics, R7) are REMOVED. */}
    <Route path='/capacity/executive' element={<Navigate to='/insights/capacity' replace />} />
    <Route path='/capacity/list' element={<Navigate to='/capacity-metrics' replace />} />
    {/* Spec 217 T037: rewritten as Dependencies Topology */}
    <Route path='/capacity/dependencies' element={<DependenciesTopologyPage />} />

    {/* Operations Screens (Feature 102-screen-completeness, 126-client-assignment-views) */}
    <Route path='/admin/clients' element={<RequireArea area='clients.client-list'><ClientsPage /></RequireArea>} />
    <Route path='/admin/clients/:id' element={<RequireArea area='clients.client-list'><ClientDetailPage /></RequireArea>} />
    {/* Spec 216 T021: the System Health stub (ComingSoonBanner, no endpoint,
        no spec) is DELETED — Platform Health is the real self-monitoring surface. */}
    <Route path='/monitoring/health' element={<Navigate to='/operations/platform-health' replace />} />

    {/* Help Screens (Feature 102-screen-completeness, 041-documentation-backend) */}
    <Route path='/help/docs' element={<DocumentationContainer />} />
    <Route path='/help/docs/guide/:slug' element={<GuideViewerPage />} />
    <Route path='/help/docs/:categoryId' element={<DocArticlePlaceholderPage />} />
    <Route path='/help/docs/:categoryId/:articleId' element={<DocArticlePlaceholderPage />} />
    <Route path='/help/runbooks' element={<RunbooksContainer />} />
    <Route path='/help/runbooks/:id' element={<RunbookPlaceholderPage />} />
    <Route path='/help/support' element={<SupportPage />} />

    {/* Backup Module New Pages (Feature 102-screen-completeness) */}
    <Route path='/backup/history' element={<Navigate to='/backup' replace />} />

    {/* Metadata Section (Feature 183 — External Metadata Enrichment) */}
    {/* Spec 285 — Analytics/Metadata is SuperAdmin-only (its detail pages are reached only
        from inside this section — verified: no Admin-visible page links into /metadata/*). */}
    <Route path='/metadata' element={<RequireArea area='analytics.metadata'><MetadataExplorerPage /></RequireArea>} />
    {/* List pages — must come BEFORE :id param routes */}
    <Route path='/metadata/semantic-models' element={<RequireArea area='analytics.metadata'><SemanticModelsListPage /></RequireArea>} />
    <Route path='/metadata/lineage' element={<RequireArea area='analytics.metadata'><LineageListPage /></RequireArea>} />
    <Route path='/metadata/refresh' element={<RequireArea area='analytics.metadata'><RefreshHistoryListPage /></RequireArea>} />
    <Route path='/metadata/schedules' element={<RequireArea area='analytics.metadata'><SchedulesListPage /></RequireArea>} />
    {/* Detail pages */}
    <Route path='/metadata/semantic-models/:id' element={<RequireArea area='analytics.metadata'><SemanticModelDefinitionPage /></RequireArea>} />
    <Route path='/metadata/items/:id/lineage' element={<RequireArea area='analytics.metadata'><LineageGraphPage /></RequireArea>} />
    <Route path='/metadata/datasets/:id/refresh' element={<RequireArea area='analytics.metadata'><RefreshHistoryPage /></RequireArea>} />
    <Route path='/metadata/items/:id/schedules' element={<RequireArea area='analytics.metadata'><ItemSchedulesPage /></RequireArea>} />
  </>
);
